import { computed, ref } from '@vue/composition-api'
import _ from 'lodash'
import store from '@/store'
import i18n from '@/libs/i18n'
import { cloneNested, parseRequestError } from '@/helpers/helpers'

const NOTIFICATION_SETTINGS_SUFFIX = 'NotificationSettings'

const removeSuffix = sectionKey => sectionKey.replace(new RegExp(`${NOTIFICATION_SETTINGS_SUFFIX}$`), '')

const translateWithFallback = (keys, fallback) => {
  const foundKey = keys.find(key => key && i18n.te(key))
  return foundKey ? i18n.t(foundKey) : fallback
}

export default () => {
  const loading = ref(false)
  const config = ref({})

  const fetchConfig = () => {
    loading.value = true
    store.dispatch('organizationConfig/fetchOrganizationConfig')
      .then(response => {
        config.value = cloneNested(response.data)
      })
      .catch(parseRequestError)
      .finally(() => {
        loading.value = false
      })
  }

  fetchConfig()

  const notificationSections = computed(() => {
    if (!_.isPlainObject(config.value)) {
      return []
    }

    return Object.entries(config.value)
      .filter(([sectionKey, sectionValue]) => sectionKey.endsWith(NOTIFICATION_SETTINGS_SUFFIX) && _.isPlainObject(sectionValue))
      .map(([sectionKey, sectionValue]) => ({
        key: sectionKey,
        value: sectionValue,
        types: Object.entries(sectionValue)
          .filter(([, typeValue]) => _.isPlainObject(typeValue))
          .map(([typeKey, typeValue]) => {
            const notificationKeys = Object.keys(typeValue || {})
            return {
              key: typeKey,
              value: typeValue,
              notifications: notificationKeys,
              paths: notificationKeys.reduce((acc, notificationKey) => {
                acc[notificationKey] = `${sectionKey}.${typeKey}.${notificationKey}`
                return acc
              }, {}),
            }
          }),
      }))
      .filter(section => section.types.length)
  })

  const getSectionLabel = sectionKey => {
    const baseKey = removeSuffix(sectionKey)
    const fallback = _.startCase(baseKey || sectionKey)

    return translateWithFallback([
      `navigation.settings.notifications.sections.${sectionKey}`,
      `navigation.settings.notifications.sections.${baseKey}`,
    ], fallback)
  }

  const getTypeLabel = typeKey => translateWithFallback([
    `navigation.settings.notifications.types.${typeKey}`,
  ], _.startCase(typeKey))

  const getNotificationLabel = (sectionKey, notificationKey) => {
    const baseKey = removeSuffix(sectionKey)

    return translateWithFallback([
      `navigation.settings.notifications.items.${sectionKey}.${notificationKey}`,
      `navigation.settings.notifications.items.${baseKey}.${notificationKey}`,
    ], _.startCase(notificationKey))
  }

  const saveValue = (path, value) => {
    _.set(config.value, path, value)
    store.dispatch('organizationConfig/patchOrganizationConfig', { path, value })
      .catch(parseRequestError)
  }

  const emptyStateLabel = computed(() => translateWithFallback([
    'navigation.settings.notifications.empty-state',
  ], 'No notification settings available yet.'))

  return {
    loading,
    notificationSections,
    getSectionLabel,
    getTypeLabel,
    getNotificationLabel,
    saveValue,
    emptyStateLabel,
  }
}
